import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { useAuth } from '@/hooks/useAuth';
import { imageApi, workflowApi } from '@/lib/api';
import { ImageRecord, ImageListQuery, TableState } from '@/types';
import { formatDate, formatFileSize, copyToClipboard, debounce, cn } from '@/lib/utils';
import { 
  Plus, 
  Search, 
  RotateCcw, 
  Edit, 
  Trash2, 
  Link, 
  Copy, 
  Check, 
  LogOut,
  Upload,
  Minimize2,
  Undo2,
} from 'lucide-react';

// 图片上传对话框组件
import ImageUploadDialog from '@/components/ImageUploadDialog';
// 图片悬浮预览组件
import { ImageHoverPreview } from '@/components/ui/image-hover-preview';

// 大小告警阈值：1MB，与 formatFileSize 保持 1024 进制一致
const OVERSIZE_THRESHOLD = 1024 * 1024;

// 压缩错误码中文化；服务端始终是权威，这里只负责让提示看得懂
const COMPRESS_ERROR_MESSAGES: Record<string, string> = {
  COMPRESSION_NO_GAIN: '这张图已经是最优编码，再压反而更大，未做任何改动。',
  COMPRESSION_ALREADY_APPLIED: '这张图当前已经是压缩版本了。如需重压请先还原原图。',
  NO_ORIGINAL_BACKUP: '这张图没压缩过，没有原图备份可还原。',
  ALREADY_ORIGINAL: '这张图当前已经是原图了。',
  COMPRESSION_UNSUPPORTED: 'GIF 与动图不支持一键压缩（重编码会丢帧）。',
  FILE_NOT_FOUND: '服务器上找不到该图片文件，请先重新上传。',
  IMAGE_NOT_FOUND: '图片记录不存在，可能已被删除。',
};

export default function ImageManagementPage() {
  const { logout } = useAuth();
  const queryClient = useQueryClient();

  // 状态管理
  const [tableState, setTableState] = useState<TableState>({
    currentPage: 1,
    pageSize: 20,
    searchTerm: '',
    selectedWorkflow: 'all',
  });

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingImage, setEditingImage] = useState<ImageRecord | null>(null);
  const [copiedUrls, setCopiedUrls] = useState<Set<string>>(new Set());
  // 只禁用正在处理的那一行，避免整表按钮一起变灰
  const [busyId, setBusyId] = useState<string | null>(null);

  // 防抖搜索
  const debouncedSearch = useMemo(
    () => debounce((searchTerm: string) => {
      setTableState(prev => ({
        ...prev,
        searchTerm,
        currentPage: 1 // 搜索时重置到第一页
      }));
    }, 300),
    []
  );

  // 查询参数
  const queryParams: ImageListQuery = {
    page: tableState.currentPage,
    limit: tableState.pageSize,
    search: tableState.searchTerm,
    workflowName: tableState.selectedWorkflow === 'all' ? undefined : tableState.selectedWorkflow,
  };

  // 获取图片列表
  const { data: imagesData, isLoading: imagesLoading, error: imagesError } = useQuery({
    queryKey: ['images', queryParams],
    queryFn: () => imageApi.getImages(queryParams),
    placeholderData: (previousData) => previousData,
  });

  // 获取工作流列表
  const { data: workflowsData } = useQuery({
    queryKey: ['workflows'],
    queryFn: () => workflowApi.getWorkflows(),
  });

  // 删除图片mutation
  const deleteMutation = useMutation({
    mutationFn: imageApi.deleteImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['images'] });
    },
    onError: (error: any) => {
      alert(`删除失败: ${error?.response?.data?.error || error.message}`);
    },
  });

  // 一键压缩mutation
  const compressMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await imageApi.compressImage(id);
      if (!result.success) {
        throw new Error(COMPRESS_ERROR_MESSAGES[result.code || ''] || result.error);
      }
      return result.data;
    },
    onSuccess: async (data) => {
      const saved = 1 - data.sizeAfter / data.sizeBefore;
      // 图片是同 key 覆盖，必须 refetch 拿到新的 updatedAt 才能刷掉浏览器里的旧字节缓存
      await queryClient.refetchQueries({ queryKey: ['images'] });
      alert(
        `${data.reused ? '已切回压缩版本' : '压缩完成'}：` +
        `${formatFileSize(data.sizeBefore)} → ${formatFileSize(data.sizeAfter)}` +
        `（省下 ${Math.round(saved * 100)}%）\n` +
        `图片链接未变。${data.reused ? '本次复用了已有的压缩结果，未重新编码。' : ''}`
      );
    },
    onError: (error: any) => {
      alert(`压缩失败: ${error.message}`);
    },
    onSettled: () => {
      setBusyId(null);
    },
  });

  // 还原原图mutation
  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await imageApi.restoreOriginal(id);
      if (!result.success) {
        throw new Error(COMPRESS_ERROR_MESSAGES[result.code || ''] || result.error);
      }
      return result.data;
    },
    onSuccess: async (data) => {
      await queryClient.refetchQueries({ queryKey: ['images'] });
      alert(
        `已还原原图：${formatFileSize(data.sizeBefore)} → ${formatFileSize(data.sizeAfter)}\n` +
        `图片链接未变。压缩结果仍保留，再次点压缩会直接切回去，不会重新压。`
      );
    },
    onError: (error: any) => {
      alert(`还原失败: ${error.message}`);
    },
    onSettled: () => {
      setBusyId(null);
    },
  });

  // 导出备份mutation
  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/backup/export', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('upload-manager-token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('导出失败');
      }
      
      const blob = await response.blob();
      // 只含元数据（manifest + data.json），几百 KB；图片字节由 COS + backup.sh 负责
      const filename = `records_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.zip`;
      saveAs(blob, filename);
    },
    onError: (error: any) => {
      alert(`导出失败: ${error.message}`);
    },
  });

  // 处理搜索
  const handleSearchChange = (value: string) => {
    debouncedSearch(value);
  };

  // 处理工作流筛选
  const handleWorkflowChange = (workflow: string) => {
    setTableState(prev => ({
      ...prev,
      selectedWorkflow: workflow,
      currentPage: 1
    }));
  };

  // 处理分页
  const handlePageChange = (page: number) => {
    setTableState(prev => ({ ...prev, currentPage: page }));
  };

  // 处理复制链接
  const handleCopyUrl = async (url: string, imageId: string) => {
    const success = await copyToClipboard(url);
    if (success) {
      setCopiedUrls(prev => new Set(prev).add(imageId));
      setTimeout(() => {
        setCopiedUrls(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageId);
          return newSet;
        });
      }, 2000);
    } else {
      alert('复制失败，请手动复制链接');
    }
  };

  // 处理一键压缩
  //
  // 压缩是不可逆的就地覆盖：外链字节被替换，所有引用该链接的下游（工作流、ComfyUI、
  // 已发布页面）看到的图都会跟着变。所以这里必须二次确认，把「仅供前端展示」这个前提
  // 交给人来判断——服务端无从知道一张图有没有被工作流当作素材依赖。
  const handleCompress = (image: ImageRecord) => {
    // 还原态下再压是「切回已有的压缩结果」，不会重新编码，措辞要如实反映
    const reusing = image.compressionState === 'restored';

    const confirmed = confirm(
      `确定${reusing ? '切回压缩版本' : '压缩这张图片'}吗？\n\n` +
      `请确认此图仅供前端展示，压缩不影响工作流。\n\n` +
      `· 图片链接、格式、像素尺寸均不变，只是这个链接吐出的字节换成压缩版\n` +
      (reusing
        ? `· 复用上次的压缩结果，不会重新编码，画质不会再降一档\n`
        : `· 原图会被完整备份，随时可用「还原原图」切回来\n`) +
      `· 当前大小：${image.sizeFormatted}`
    );

    if (confirmed) {
      setBusyId(image.id);
      compressMutation.mutate(image.id);
    }
  };

  // 处理还原原图
  //
  // 还原的是字节不是地址：链接自始至终只有一个，所以这同样会改变所有下游看到的图，
  // 与压缩一样需要二次确认。
  const handleRestore = (image: ImageRecord) => {
    const confirmed = confirm(
      `确定把这张图还原成原图吗？\n\n` +
      `· 图片链接不变，只是这个链接吐出的字节换回压缩前的原图\n` +
      `· 体积会变回原来的大小，用它的前端页面会重新变重\n` +
      `· 压缩结果会保留，之后点压缩可直接切回去，无需重新压缩\n` +
      `· 当前大小：${image.sizeFormatted}`
    );

    if (confirmed) {
      setBusyId(image.id);
      restoreMutation.mutate(image.id);
    }
  };

  // 处理删除
  const handleDelete = async (image: ImageRecord) => {
    if (confirm(`确定删除归属"${image.workflowName}"的记录吗？此操作不可撤销。`)) {
      deleteMutation.mutate(image.id);
    }
  };

  // 处理编辑
  const handleEdit = (image: ImageRecord) => {
    setEditingImage(image);
    setShowUploadDialog(true);
  };

  // 处理对话框关闭
  const handleDialogClose = () => {
    setShowUploadDialog(false);
    setEditingImage(null);
  };

  // 获取分页信息
  const images = imagesData?.success ? imagesData.data.images : [];
  const totalPages = imagesData?.success ? imagesData.data.totalPages : 0;
  const total = imagesData?.success ? imagesData.data.total : 0;
  const workflows = workflowsData?.success ? workflowsData.data.workflows : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="/logo.png" alt="Logo" className="h-8 w-8 mr-3" />
              <h1 className="text-2xl font-semibold text-gray-900">图片上传管理器</h1>
            </div>
            <Button variant="outline" onClick={logout} className="flex items-center space-x-2 hover:bg-red-50 hover:border-red-200 hover:text-red-600">
              <LogOut className="h-4 w-4" />
              <span>退出登录</span>
            </Button>
          </div>
        </div>
      </header>

      {/* 主内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-end gap-4">
                <CardTitle className="text-xl">图片管理</CardTitle>
                <span className="text-sm text-gray-500">
                  共 {total} 条图片记录
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => exportMutation.mutate()}
                  disabled={exportMutation.isPending}
                  className="flex items-center space-x-2"
                  title="导出全部图片记录（manifest + data.json），不含图片字节"
                >
                  {exportMutation.isPending ? (
                    <RotateCcw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  <span>导出记录</span>
                </Button>

                <Button
                  onClick={() => setShowUploadDialog(true)}
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700"
                >
                  <Plus className="h-4 w-4" />
                  <span>添加图片</span>
                </Button>
              </div>
            </div>

            {/* 操作工具栏 */}
            <div className="flex flex-col lg:flex-row gap-4 pt-4">
              {/* 搜索框 */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="搜索访问链接、归属工作流..."
                    className="pl-10"
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </div>
              </div>

              {/* 工作流筛选 */}
              <div className="flex items-center space-x-2">
                <Label className="text-sm font-medium">工作流:</Label>
                <Select value={tableState.selectedWorkflow} onValueChange={handleWorkflowChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部工作流</SelectItem>
                    {workflows.map((workflow) => (
                      <SelectItem key={workflow} value={workflow}>
                        {workflow}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 重置按钮 */}
              <Button
                variant="outline"
                onClick={() => {
                  setTableState({
                    currentPage: 1,
                    pageSize: 20,
                    searchTerm: '',
                    selectedWorkflow: 'all',
                  });
                  // 清空搜索框
                  const searchInput = document.querySelector('input[placeholder*="搜索"]') as HTMLInputElement;
                  if (searchInput) searchInput.value = '';
                }}
                className="flex items-center space-x-2"
              >
                <RotateCcw className="h-4 w-4" />
                <span>重置</span>
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {/* 错误状态 */}
            {imagesError && (
              <div className="text-center py-8 text-red-600">
                加载失败，请稍后重试
              </div>
            )}

            {/* 加载状态 */}
            {imagesLoading && (
              <div className="text-center py-8">
                <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <div className="mt-2 text-gray-600">加载中...</div>
              </div>
            )}

            {/* 图片表格 */}
            {!imagesLoading && !imagesError && (
              <>
                <div className="overflow-x-auto">
                  <Table className="min-w-[1000px]">
                    <TableHeader>
                      <TableRow>
                        {/* 340px = 域名行 28 个等宽字符(约 235px) + code 内边距 + 图标 + 复制按钮 + cell padding。
                            窄于此域名行会二次折行，链接就变成三行 */}
                        <TableHead className="min-w-[340px] w-[340px]">访问链接</TableHead>
                        <TableHead className="min-w-[130px] w-[130px]">归属工作流</TableHead>
                        <TableHead className="text-center min-w-[80px] w-[80px]">图片</TableHead>
                        <TableHead className="text-center min-w-[90px] w-[90px]">分辨率</TableHead>
                        <TableHead className="text-center min-w-[70px] w-[70px]">大小</TableHead>
                        <TableHead className="text-center min-w-[120px] w-[120px]">创建时间</TableHead>
                        {/* 4 个图标按钮（压缩/还原/编辑/删除）各 32px + 间距，再加 cell 的 p-4 */}
                        <TableHead className="text-center min-w-[180px] w-[180px]">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {images.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            暂无数据
                          </TableCell>
                        </TableRow>
                      ) : (
                        images.map((image: ImageRecord) => (
                          <TableRow key={image.id}>
                            <TableCell>
                              <div className="flex items-center space-x-2 min-w-[300px]">
                                <Link className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  {/* 换行点由下面的 <br/> 显式指定（域名 / 路径各一行）。
                                      whitespace-nowrap 是为了禁止这两行再被自动折断——URL 里
                                      浏览器不把 '/' 当换行机会，一旦列宽不足就会从中间断开，
                                      变成三行且断在无意义的位置。列宽不够时 nowrap 会撑开列，
                                      由外层 overflow-x-auto 兜底，不会与相邻列重叠。 */}
                                  <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono leading-relaxed block whitespace-nowrap">
                                    {(() => {
                                      const url = image.accessUrl;
                                      const match = url.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
                                      if (match) {
                                        const [, domain, path] = match;
                                        return (
                                          <>
                                            {domain}
                                            {path && <><br/>{path}</>}
                                          </>
                                        );
                                      }
                                      return url;
                                    })()}
                                  </code>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCopyUrl(image.accessUrl, image.id)}
                                  className="p-1 h-6 w-6 flex-shrink-0"
                                  title="复制链接"
                                >
                                  {copiedUrls.has(image.id) ? (
                                    <Check className="h-3 w-3 text-green-600" />
                                  ) : (
                                    <Copy className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-gray-900">
                              {image.workflowName}
                            </TableCell>
                            <TableCell className="text-center">
                              <ImageHoverPreview
                                src={`${image.accessUrl}?t=${new Date(image.updatedAt).getTime()}`}
                                alt={`${image.originalName} (${image.resolutionFormatted})`}
                                previewSize="md"
                              >
                                <img
                                  src={`${image.accessUrl}?t=${new Date(image.updatedAt).getTime()}`}
                                  alt={image.originalName}
                                  className="w-12 h-12 object-cover rounded-lg mx-auto border hover:border-blue-400 hover:shadow-md transition-all duration-200"
                                  loading="lazy"
                                />
                              </ImageHoverPreview>
                            </TableCell>
                            <TableCell className="text-center text-sm text-gray-600">
                              {image.resolutionFormatted}
                            </TableCell>
                            <TableCell
                              className={cn(
                                'text-center text-sm',
                                image.size > OVERSIZE_THRESHOLD
                                  ? 'text-red-600 font-medium'
                                  : 'text-gray-600'
                              )}
                              title={image.size > OVERSIZE_THRESHOLD ? '用于前端显示时，请注意优化图像大小' : undefined}
                            >
                              {image.sizeFormatted}
                            </TableCell>
                            <TableCell className="text-center text-sm text-gray-600">
                              {formatDate(image.createdAt)}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center space-x-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(image)}
                                  className="p-2 hover:bg-red-50 text-red-600"
                                  title="编辑"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCompress(image)}
                                  className={cn(
                                    'p-2',
                                    image.compressionState === 'compressed'
                                      ? 'text-gray-300'
                                      : 'hover:bg-emerald-50 text-emerald-600'
                                  )}
                                  disabled={
                                    busyId === image.id || image.compressionState === 'compressed'
                                  }
                                  title={
                                    image.compressionState === 'compressed'
                                      ? '当前已是压缩版本'
                                      : image.compressionState === 'restored'
                                        ? '切回压缩版本（复用已有结果，不会重新编码）'
                                        : '一键压缩（链接不变，仅重新编码降低体积）'
                                  }
                                >
                                  {busyId === image.id ? (
                                    <RotateCcw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Minimize2 className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRestore(image)}
                                  className={cn(
                                    'p-2',
                                    image.compressionState === 'compressed'
                                      ? 'hover:bg-amber-50 text-amber-600'
                                      : 'text-gray-300'
                                  )}
                                  disabled={
                                    busyId === image.id || image.compressionState !== 'compressed'
                                  }
                                  title={
                                    image.compressionState === 'compressed'
                                      ? '还原原图（链接不变，换回压缩前的字节）'
                                      : '没有可还原的原图'
                                  }
                                >
                                  <Undo2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(image)}
                                  className="p-2 hover:bg-red-50 text-red-600"
                                  disabled={deleteMutation.isPending}
                                  title="删除"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* 分页 */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                    <div className="text-sm text-gray-500 whitespace-nowrap">
                      显示第 {Math.min((tableState.currentPage - 1) * tableState.pageSize + 1, total)} - {Math.min(tableState.currentPage * tableState.pageSize, total)} 条，共 {total} 条记录
                    </div>
                    <Pagination>
                      <PaginationContent>
                        {/* 上一页 */}
                        <PaginationItem>
                          <PaginationPrevious 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (tableState.currentPage > 1) {
                                handlePageChange(tableState.currentPage - 1);
                              }
                            }}
                            className={tableState.currentPage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>

                        {/* 页码 */}
                        {(() => {
                          const pages = [];
                          const maxVisiblePages = 5;
                          
                          if (totalPages <= maxVisiblePages) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            if (tableState.currentPage <= 3) {
                              for (let i = 1; i <= 5; i++) {
                                pages.push(i);
                              }
                            } else if (tableState.currentPage >= totalPages - 2) {
                              for (let i = totalPages - 4; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              for (let i = tableState.currentPage - 2; i <= tableState.currentPage + 2; i++) {
                                pages.push(i);
                              }
                            }
                          }
                          
                          return pages.map((page) => (
                            <PaginationItem key={page}>
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(page);
                                }}
                                isActive={tableState.currentPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          ));
                        })()}

                        {/* 下一页 */}
                        <PaginationItem>
                          <PaginationNext 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (tableState.currentPage < totalPages) {
                                handlePageChange(tableState.currentPage + 1);
                              }
                            }}
                            className={tableState.currentPage >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* 图片上传/编辑对话框 */}
        <ImageUploadDialog
          open={showUploadDialog}
          onOpenChange={handleDialogClose}
          editingImage={editingImage}
          workflows={workflows}
          onSuccess={async () => {
            // 强制重新获取数据，确保图片刷新
            await queryClient.refetchQueries({ queryKey: ['images'] });
            await queryClient.refetchQueries({ queryKey: ['workflows'] });
          }}
        />

      </main>
    </div>
  );
}
